import { createRootRouteWithContext, Outlet, useMatches } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import {
  DocumentDirectionProvider,
  formatDocumentTitle,
  Toaster,
  useDocumentLanguage,
  useDocumentTitle,
} from '@hetivo/hetivo-lib-web-utils';
import type { PermissionsContextValue } from '@hetivo/hetivo-lib-front-utils';
import type { AuthContextValue } from '&/auth';
import type { TenantModulesResult } from '&/hooks/use-tenant-modules';

export interface RouterContext {
  auth: AuthContextValue;
  entitlement: TenantModulesResult;
  permissions: PermissionsContextValue;
}

/**
 * Keeps the browser tab title in sync with the active route.
 *
 * Each page declares its title on the route via `staticData.titleKey` (resolved
 * against the `documentTitle` i18n namespace); the deepest matching route wins
 * and the app name (`app.title`) is appended. Routes that title themselves —
 * e.g. an entity detail page that shows the loaded record's name — opt out with
 * `staticData.dynamic`, in which case this writes nothing and defers to them.
 */
function useRouteDocumentTitle(): void {
  const { t } = useTranslation();
  const matches = useMatches();

  const ownsItsTitle = matches.some((match) => match.staticData.dynamic);
  const titleKey = [...matches].reverse().find((match) => match.staticData.titleKey)?.staticData.titleKey;

  const title = ownsItsTitle
    ? null
    : formatDocumentTitle(t('app.title'), titleKey ? [t(`documentTitle.${titleKey}`)] : []);

  useDocumentTitle(title);
}

function RootLayout(): React.JSX.Element {
  useDocumentLanguage();
  useRouteDocumentTitle();
  return (
    <DocumentDirectionProvider>
      <Outlet />
      <Toaster />
    </DocumentDirectionProvider>
  );
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
});
